import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import {
  DIRECTION_HORIZONTAL,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_VERTICAL,
} from 'hammerjs';
import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import {
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  HammerModule,
} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  IonicRouteStrategy,
  isPlatform,
  getPlatforms,
} from '@ionic/angular/standalone';
import { RollbarErrorHandler, RollbarService, rollbarFactory } from './rollbar';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserModule } from '@angular/platform-browser';
import { type IonicConfig } from '@ionic/core';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import { PaddleModule } from '@saschwarz/ngx-paddle-wrapper';
import {
  IonApp,
  IonSplitPane,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TourIonPopoverModule } from 'ngx-ui-tour-ionic';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
declare let Hammer: any;

@Injectable({
  providedIn: 'root',
})
export class DoubleTapGestureConfig extends HammerGestureConfig {
  override overrides = {
    tap: { taps: 2 },
    // https://github.com/angular/angular/issues/10328
    pan: {
      enable: true,
      threshold: 1,
      direction:
        DIRECTION_HORIZONTAL |
        DIRECTION_LEFT |
        DIRECTION_RIGHT |
        DIRECTION_VERTICAL,
    },
  };
  override options = { domEvents: true };
}

const getConfig = (): IonicConfig => {
  const isDesktop = modifiedIsDesktop();
  if (isDesktop) {
    return {
      mode: 'ios',
    };
  }
  return {};
};

@Injectable()
export class LocalErrorHandler implements ErrorHandler {
  handleError(error: Error) {
    console.error(error);
  }
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    AuthModule.forRoot({ ...environment.auth0Config }),
    BrowserModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    HammerModule,
    PaddleModule.forRoot({ sandbox: environment.paddle.sandbox }),
    ServiceWorkerModule.register('acmaster-worker.js', {
      enabled: environment.production, // staging and production deployments
    }),
    SharedModule,
    IonApp,
    IonSplitPane,
    TourIonPopoverModule,
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: DoubleTapGestureConfig,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useClass: environment.production
        ? RollbarErrorHandler
        : LocalErrorHandler,
    },
    { provide: RollbarService, useFactory: rollbarFactory },
    DoubleTapGestureConfig,
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular(getConfig()),
  ],
})
export class AppModule {}

export function modifiedIsDesktop() {
  console.debug('userAgent', window.navigator.userAgent);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.debug('platform', (window.navigator as any)?.userAgentData?.platform); // Windows or macOS or undefined on Safari/Firefox desktop
  console.debug('platforms', getPlatforms());
  let isDesktop = isPlatform('desktop');
  if (!isDesktop) {
    // Windows is not detected on Safari/Firefox desktop
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const platform = (window.navigator as any)?.userAgentData?.platform;
    if (platform === 'Windows') {
      isDesktop = true;
    } else if (!platform) {
      // Firefox desktop doesn't support userAgentData
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userAgent = (window.navigator as any).userAgent;
      if (userAgent.includes('Windows')) {
        isDesktop = true;
      }
    }
  }
  return isDesktop;
}
